import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Activity, AlertTriangle, CheckCircle2, Clock, Database, Globe,
  MonitorSmartphone, Pause, Play, RefreshCw, Search, Shield, Radio, TrendingUp,
  Zap, XCircle, Loader2,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  ScraperActiveRun, ScraperAttentionItem, ScraperStatus, ScraperStatusResponse,
  ScraperVenueStatus, fetchScraperStatus, triggerScraperRun,
} from '../services/api';
import LazyImage from './LazyImage';

const REFRESH_SECONDS = 30;
// While a scrape is in flight the page is being watched, so poll hard enough
// to see venues turn over.
const ACTIVE_REFRESH_SECONDS = 5;

/* ------------------------------------------------------------------ */
/* Status vocabulary                                                    */
/* ------------------------------------------------------------------ */

interface StatusStyle {
  label: string;
  dot: string;
  text: string;
  chipBg: string;
  chipText: string;
  bar: string;
}

const STATUS_STYLES: Record<ScraperStatus, StatusStyle> = {
  running: {
    label: 'Scraping…', dot: 'bg-blue-500', text: 'text-blue-700',
    chipBg: 'bg-blue-50 border-blue-200', chipText: 'text-blue-700',
    bar: 'bg-blue-500',
  },
  healthy: {
    // "Available" not "Live": this describes the stored availability for the
    // venue, not whether a scrape is running right now.
    label: 'Available', dot: 'bg-emerald-500', text: 'text-emerald-700',
    chipBg: 'bg-emerald-50 border-emerald-200', chipText: 'text-emerald-700',
    bar: 'bg-emerald-500',
  },
  no_availability: {
    label: 'Fully booked', dot: 'bg-amber-500', text: 'text-amber-700',
    chipBg: 'bg-amber-50 border-amber-200', chipText: 'text-amber-700',
    bar: 'bg-amber-400',
  },
  unavailable: {
    label: 'Not listed', dot: 'bg-slate-400', text: 'text-slate-600',
    chipBg: 'bg-slate-50 border-slate-200', chipText: 'text-slate-600',
    bar: 'bg-slate-300',
  },
  stale: {
    label: 'Stale', dot: 'bg-orange-500', text: 'text-orange-700',
    chipBg: 'bg-orange-50 border-orange-200', chipText: 'text-orange-700',
    bar: 'bg-orange-500',
  },
  never_checked: {
    label: 'Never run', dot: 'bg-violet-500', text: 'text-violet-700',
    chipBg: 'bg-violet-50 border-violet-200', chipText: 'text-violet-700',
    bar: 'bg-violet-400',
  },
  error: {
    label: 'Failing', dot: 'bg-rose-500', text: 'text-rose-700',
    chipBg: 'bg-rose-50 border-rose-200', chipText: 'text-rose-700',
    bar: 'bg-rose-500',
  },
  paused: {
    label: 'Paused', dot: 'bg-gray-300', text: 'text-gray-500',
    chipBg: 'bg-gray-50 border-gray-200', chipText: 'text-gray-500',
    bar: 'bg-gray-300',
  },
};

const STATUS_ORDER: ScraperStatus[] = [
  'running', 'healthy', 'no_availability', 'stale', 'error', 'never_checked',
  'unavailable', 'paused',
];

const SEVERITY_STYLES: Record<string, { chip: string; dot: string; label: string }> = {
  high: { chip: 'bg-rose-50 border-rose-200 text-rose-700', dot: 'bg-rose-500', label: 'Needs a fix' },
  medium: { chip: 'bg-amber-50 border-amber-200 text-amber-700', dot: 'bg-amber-500', label: 'Worth checking' },
  low: { chip: 'bg-slate-50 border-slate-200 text-slate-600', dot: 'bg-slate-400', label: 'For info' },
};

/* ------------------------------------------------------------------ */
/* Formatting                                                           */
/* ------------------------------------------------------------------ */

const formatAge = (seconds: number | null | undefined): string => {
  if (seconds === null || seconds === undefined) return '—';
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
};

const formatDuration = (ms: number | null | undefined): string => {
  if (ms === null || ms === undefined) return '—';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
};

const formatBytes = (bytes: number): string => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const shortDay = (date: string): string => {
  const parsed = new Date(`${date}T00:00:00`);
  return Number.isNaN(parsed.getTime())
    ? date.slice(5)
    : parsed.toLocaleDateString('en-GB', { weekday: 'short' });
};

/* ------------------------------------------------------------------ */
/* Small pieces                                                         */
/* ------------------------------------------------------------------ */

const LiveDot: React.FC<{ className?: string; pulse?: boolean }> = ({ className = '', pulse }) => (
  <span className="relative inline-flex h-2 w-2 flex-shrink-0">
    {pulse && (
      <span className={`absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping ${className}`} />
    )}
    <span className={`relative inline-flex h-2 w-2 rounded-full ${className}`} />
  </span>
);

const MicroLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-gray-400">
    {children}
  </span>
);

interface MetricProps {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  detail?: React.ReactNode;
  accent?: string;
}

const Metric: React.FC<MetricProps> = ({ icon, label, value, detail, accent = 'text-blue-600 bg-blue-50' }) => (
  <div className="relative bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 p-6 overflow-hidden">
    {/* faint instrument grid, keeps the panel feeling technical without shouting */}
    <div
      className="absolute inset-0 opacity-[0.035] pointer-events-none"
      style={{
        backgroundImage:
          'linear-gradient(to right, #0f172a 1px, transparent 1px), linear-gradient(to bottom, #0f172a 1px, transparent 1px)',
        backgroundSize: '14px 14px',
      }}
    />
    <div className="relative flex items-start gap-4">
      <div className={`p-2.5 rounded-lg flex-shrink-0 ${accent}`}>{icon}</div>
      <div className="min-w-0">
        <MicroLabel>{label}</MicroLabel>
        <p className="text-2xl font-semibold text-gray-900 mt-1.5 tabular-nums leading-none">
          {value}
        </p>
        {detail && <p className="text-xs text-gray-500 mt-2">{detail}</p>}
      </div>
    </div>
  </div>
);

/** Seven-day availability bars — the "is this venue actually producing data" read. */
const DayBars: React.FC<{ days: ScraperVenueStatus['days']; tone: string }> = ({ days, tone }) => {
  if (!days.length) {
    return <span className="text-xs text-gray-300">no upcoming slots</span>;
  }
  const peak = Math.max(...days.map((d) => d.slots), 1);

  return (
    <div className="flex items-end gap-1 h-9" role="img" aria-label="Slots per upcoming day">
      {days.map((day) => (
        <div key={day.date} className="group/bar relative flex flex-col items-center gap-1">
          <div
            className={`w-3 rounded-sm ${tone} transition-all duration-300`}
            style={{ height: `${Math.max(3, (day.slots / peak) * 26)}px` }}
          />
          <span className="text-[9px] text-gray-400 leading-none">{shortDay(day.date)}</span>
          <span className="pointer-events-none absolute -top-7 hidden group-hover/bar:block whitespace-nowrap rounded bg-gray-900 px-1.5 py-1 text-[10px] font-medium text-white shadow-lg z-10">
            {day.slots} slot{day.slots === 1 ? '' : 's'}
          </span>
        </div>
      ))}
    </div>
  );
};

/** Party sizes a manual run offers. Mirrors the API's own bounds. */
const PARTY_SIZES = [1, 2, 3, 4, 5, 6];

/**
 * Kick off a scrape by hand, for one party size.
 *
 * The scheduled run only ever covers a party of two, so anything else has to
 * be asked for - this is how a party of three got onto the site at all.
 */
const ManualRunControl: React.FC<{
  disabled: boolean;
  onStarted: () => void;
}> = ({ disabled, onStarted }) => {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<number | null>(null);
  const [message, setMessage] = useState<{ text: string; ok: boolean } | null>(null);

  const start = async (partySize: number) => {
    setPending(partySize);
    setMessage(null);
    try {
      const result = await triggerScraperRun(partySize);
      setMessage({ text: result.message, ok: true });
      setOpen(false);
      onStarted();
    } catch (err: any) {
      setMessage({
        text:
          err?.response?.data?.error ||
          'Could not start the scrape. Check the server logs.',
        ok: false,
      });
    } finally {
      setPending(null);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => { setOpen((v) => !v); setMessage(null); }}
        disabled={disabled}
        title={disabled ? 'A scrape is already running' : 'Start a scrape'}
        className={`flex items-center px-5 py-2.5 rounded-lg text-sm font-medium
                   transition-colors duration-200
                   ${disabled
                     ? 'text-gray-400 bg-gray-50 cursor-not-allowed'
                     : 'text-emerald-700 bg-emerald-50 hover:bg-emerald-100'}`}
      >
        <Play className="w-4 h-4 mr-2" />
        Run scrape
      </button>

      {open && !disabled && (
        <div className="absolute right-0 mt-2 z-30 w-56 rounded-xl border border-gray-200
                        bg-white shadow-lg p-3">
          <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400 mb-2">
            Party size
          </p>
          <div className="grid grid-cols-3 gap-1.5">
            {PARTY_SIZES.map((size) => (
              <button
                key={size}
                onClick={() => start(size)}
                disabled={pending !== null}
                className="flex items-center justify-center h-10 rounded-lg border
                           border-gray-200 text-sm font-semibold text-gray-700
                           hover:border-emerald-300 hover:bg-emerald-50
                           disabled:opacity-50 disabled:cursor-not-allowed
                           transition-colors duration-150"
              >
                {pending === size
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : size}
              </button>
            ))}
          </div>
          <p className="text-[10px] text-gray-400 mt-2 leading-relaxed">
            Runs London, 7 days ahead. Takes a while - the page follows along.
          </p>
        </div>
      )}

      {message && (
        <p
          className={`absolute right-0 mt-2 whitespace-nowrap text-[11px]
                     ${message.ok ? 'text-emerald-600' : 'text-rose-600'}`}
        >
          {message.text}
        </p>
      )}
    </div>
  );
};

/** Banner shown only while a run is actually in flight. */
const ActiveRunBanner: React.FC<{ run: ScraperActiveRun }> = ({ run }) => {
  const progress = run.venues_total
    ? Math.round((run.venues_done / run.venues_total) * 100)
    : 0;

  return (
    <div className="relative bg-white rounded-xl shadow-sm border border-blue-200 p-6 mb-6 overflow-hidden">
      <div className="absolute inset-x-0 top-0 h-0.5 bg-blue-100">
        <div
          className="h-full bg-blue-500 transition-all duration-700"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <LiveDot className="bg-blue-500" pulse />
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900">
              Scrape in progress
              {run.party_sizes.length > 0 && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full
                                 bg-blue-50 text-[11px] font-semibold text-blue-700
                                 ring-1 ring-inset ring-blue-100">
                  {run.party_sizes.map((n) => `party of ${n}`).join(', ')}
                </span>
              )}
              <span className="ml-2 font-mono text-[11px] text-gray-400">{run.run_id}</span>
            </p>
            <p className="text-xs text-gray-500 mt-0.5 tabular-nums">
              {run.venues_done}/{run.venues_total} venues · {run.slots_so_far} slots so far
              {run.errors_so_far > 0 && ` · ${run.errors_so_far} failed`}
              {run.elapsed_seconds !== null && ` · running ${formatDuration(run.elapsed_seconds * 1000)}`}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {run.now_checking.map((item) => (
            <span
              key={`${item.provider}:${item.slug}`}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-blue-200 bg-blue-50 text-[11px] text-blue-700"
            >
              <Loader2 className="w-3 h-3 animate-spin" />
              <span className="font-mono">{item.slug}</span>
              {item.for_seconds !== null && (
                <span className="text-blue-400 tabular-nums">{item.for_seconds}s</span>
              )}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

/** Actionable problems, grouped so 50 "never checked" rows don't bury a real failure. */
const AttentionPanel: React.FC<{ items: ScraperAttentionItem[] }> = ({ items }) => {
  const [expanded, setExpanded] = useState(false);

  if (!items.length) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 mb-6 flex items-center gap-3">
        <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
        <div>
          <p className="text-sm font-medium text-gray-900">Nothing needs attention</p>
          <p className="text-xs text-gray-500 mt-0.5">
            Every source is checking cleanly.
          </p>
        </div>
      </div>
    );
  }

  const high = items.filter((i) => i.severity === 'high');
  const rest = items.filter((i) => i.severity !== 'high');
  const shown = expanded ? items : high.length ? high : items.slice(0, 3);

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
          <MicroLabel>Needs attention</MicroLabel>
        </div>
        <div className="flex items-center gap-3 text-[11px] tabular-nums">
          {(['high', 'medium', 'low'] as const).map((sev) => {
            const count = items.filter((i) => i.severity === sev).length;
            if (!count) return null;
            return (
              <span key={sev} className="flex items-center gap-1.5 text-gray-500">
                <LiveDot className={SEVERITY_STYLES[sev].dot} />
                {count} {SEVERITY_STYLES[sev].label.toLowerCase()}
              </span>
            );
          })}
        </div>
      </div>

      <div className="space-y-2">
        {shown.map((item) => (
          <div
            key={`${item.id}-${item.issue}`}
            className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 hover:border-gray-200 transition-colors"
          >
            <LiveDot className={`${SEVERITY_STYLES[item.severity].dot} mt-1.5`} />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="text-sm font-medium text-gray-900">{item.venue}</span>
                <span className="text-[11px] font-mono text-gray-400">
                  {item.provider}:{item.slug}
                </span>
              </div>
              <p className="text-xs text-gray-700 mt-0.5">{item.issue}</p>
              {item.detail && (
                <p className="text-[11px] text-gray-400 mt-0.5 truncate" title={item.detail}>
                  {item.detail}
                </p>
              )}
              <p className="text-[11px] text-gray-500 mt-1 italic">{item.action}</p>
            </div>
          </div>
        ))}
      </div>

      {(rest.length > 0 || items.length > shown.length) && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-3 text-xs text-blue-600 hover:text-blue-700 transition-colors"
        >
          {expanded
            ? 'Show fewer'
            : `Show all ${items.length} items`}
        </button>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Venue row                                                            */
/* ------------------------------------------------------------------ */

const VenueRow: React.FC<{ venue: ScraperVenueStatus }> = ({ venue }) => {
  const style = STATUS_STYLES[venue.status] ?? STATUS_STYLES.paused;

  return (
    <div className="group grid grid-cols-12 gap-4 items-center px-5 py-4 border-t border-gray-50 hover:bg-blue-50/30 transition-colors duration-200">
      {/* Venue identity */}
      <div className="col-span-12 md:col-span-4 flex items-center gap-3 min-w-0">
        <LiveDot
          className={style.dot}
          pulse={venue.status === 'healthy' || venue.status === 'running'}
        />
        <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 ring-1 ring-gray-100">
          <LazyImage
            src={venue.proxied_image || venue.image || '/default_lazy_restaurant.png'}
            alt={venue.venue}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{venue.venue}</p>
          <p className="text-[11px] text-gray-400 font-mono truncate">
            {venue.provider}:{venue.slug}
          </p>
        </div>
      </div>

      {/* Status */}
      <div className="col-span-6 md:col-span-2">
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-[11px] font-medium ${style.chipBg} ${style.chipText}`}>
          {style.label}
        </span>
        {venue.unavailable_reason && (
          <p className="text-[10px] text-gray-400 mt-1 truncate" title={venue.unavailable_reason}>
            {venue.unavailable_reason}
          </p>
        )}
        {venue.last_error && (
          <p className="text-[10px] text-rose-500 mt-1 truncate" title={venue.last_error}>
            {venue.last_error}
          </p>
        )}
      </div>

      {/* Slot counts */}
      <div className="col-span-6 md:col-span-2">
        <p className="text-lg font-semibold text-gray-900 tabular-nums leading-none">
          {venue.slots}
        </p>
        <p className="text-[10px] text-gray-400 mt-1">
          {venue.bookable} bookable
          {venue.requestable > 0 && ` · ${venue.requestable} request`}
        </p>
      </div>

      {/* Seven-day shape */}
      <div className="col-span-6 md:col-span-2">
        <DayBars days={venue.days} tone={style.bar} />
      </div>

      {/* Timing, or live progress while in flight */}
      <div className="col-span-6 md:col-span-2 text-right">
        {venue.status === 'running' ? (
          <>
            <p className="text-xs text-blue-600 tabular-nums flex items-center justify-end gap-1.5">
              <Loader2 className="w-3 h-3 animate-spin" />
              running {venue.running_for_seconds ?? 0}s
            </p>
            <p className="text-[10px] text-gray-400 mt-0.5">in progress</p>
          </>
        ) : (
          <>
            <p className="text-xs text-gray-600 tabular-nums">
              {formatAge(venue.last_checked_age_seconds)}
            </p>
            <p className="text-[10px] text-gray-400 tabular-nums mt-0.5">
              {venue.last_check_status === 'skipped'
                ? 'skipped · cached'
                : `took ${formatDuration(venue.last_duration_ms)}`}
            </p>
            {venue.consecutive_failures > 0 ? (
              <p className="text-[10px] text-rose-500 tabular-nums mt-0.5">
                {venue.consecutive_failures} fail{venue.consecutive_failures === 1 ? '' : 's'} in a row
              </p>
            ) : venue.checks_recorded >= 3 && venue.success_rate !== null &&
              venue.success_rate < 1 ? (
              <p className="text-[10px] text-gray-400 tabular-nums mt-0.5">
                {Math.round(venue.success_rate * 100)}% of last {venue.checks_recorded} ok
              </p>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Dashboard                                                            */
/* ------------------------------------------------------------------ */

const ScraperDashboard: React.FC = () => {
  const [data, setData] = useState<ScraperStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [countdown, setCountdown] = useState(REFRESH_SECONDS);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<ScraperStatus | 'all'>('all');
  const [providerFilter, setProviderFilter] = useState<string>('all');

  // Keeps the polling effect from re-subscribing on every render.
  const loadRef = useRef<() => void>(() => {});
  const intervalRef = useRef<number>(REFRESH_SECONDS);

  const load = useCallback(async () => {
    try {
      setIsRefreshing(true);
      const response = await fetchScraperStatus();
      setData(response);
      intervalRef.current = response.active_run
        ? ACTIVE_REFRESH_SECONDS
        : REFRESH_SECONDS;
      setError(null);
    } catch (err: any) {
      setError(
        err?.response?.status === 403
          ? 'Your account does not have access to scraper monitoring.'
          : 'Could not reach the scraper status service.'
      );
    } finally {
      setLoading(false);
      setIsRefreshing(false);
      setCountdown(intervalRef.current);
    }
  }, []);

  loadRef.current = load;

  useEffect(() => {
    loadRef.current();
    const tick = setInterval(() => {
      setCountdown((current) => {
        if (current <= 1) {
          loadRef.current();
          return intervalRef.current;
        }
        return current - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, []);

  const venues = useMemo(() => {
    if (!data) return [];
    const needle = query.trim().toLowerCase();
    return data.venues.filter((venue) => {
      if (statusFilter !== 'all' && venue.status !== statusFilter) return false;
      if (providerFilter !== 'all' && venue.provider !== providerFilter) return false;
      if (!needle) return true;
      return (
        venue.venue.toLowerCase().includes(needle) ||
        venue.slug.toLowerCase().includes(needle) ||
        venue.provider.toLowerCase().includes(needle)
      );
    });
  }, [data, query, statusFilter, providerFilter]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
        <p className="text-sm text-gray-400 tracking-wide">Connecting to scrapers…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="bg-white border border-rose-100 rounded-xl shadow-sm p-8 max-w-md text-center">
          <XCircle className="w-10 h-10 text-rose-400 mx-auto mb-4" />
          <p className="font-medium text-gray-900 mb-2">Monitoring unavailable</p>
          <p className="text-sm text-gray-500 mb-5">{error}</p>
          <button
            onClick={load}
            className="text-sm px-4 py-2 rounded-lg text-blue-600 bg-blue-50 hover:bg-blue-100 transition-colors"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { totals, database } = data;
  const statusCounts = totals.by_status || {};
  const healthPercent = totals.sources
    ? Math.round((totals.healthy / totals.sources) * 100)
    : 0;

  return (
    <div className="container mx-auto max-w-[1400px]">
      {/* ---------------- Header ---------------- */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5 mb-10">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Shield className="w-3.5 h-3.5 text-blue-500" />
            <MicroLabel>Admin · Reservation infrastructure</MicroLabel>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
            Scraper Control Room
          </h1>
          <p className="text-sm text-gray-500 mt-2 font-light">
            Live health of every booking-site scraper feeding the availability feed.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className={`flex items-center gap-2 px-3.5 py-2 rounded-lg bg-white border shadow-sm ${
            data.active_run ? 'border-blue-200' : 'border-gray-100'
          }`}>
            <LiveDot className={data.active_run ? 'bg-blue-500' : 'bg-emerald-500'} pulse />
            <span className="text-xs text-gray-500 tabular-nums">
              {data.active_run ? 'scraping · ' : ''}auto-refresh in {countdown}s
            </span>
          </div>
          <Link
            to="/dashboard/admin"
            className="flex items-center px-5 py-2.5 rounded-lg text-sm font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 transition-colors duration-200"
          >
            <TrendingUp className="w-4 h-4 mr-2" />
            Analytics
          </Link>
          <ManualRunControl
            disabled={data.active_run !== null}
            onStarted={load}
          />
          <button
            onClick={load}
            disabled={isRefreshing}
            className={`flex items-center px-5 py-2.5 rounded-lg text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 transition-colors duration-200 ${
              isRefreshing ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* ---------------- Live run ---------------- */}
      {data.active_run && <ActiveRunBanner run={data.active_run} />}

      {/* ---------------- Needs attention ---------------- */}
      <AttentionPanel items={data.attention} />

      {/* ---------------- Metrics ---------------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 mb-6">
        <Metric
          icon={<Activity className="w-5 h-5" />}
          label="Sources healthy"
          value={`${totals.healthy}/${totals.sources}`}
          detail={`${healthPercent}% returning live availability`}
          accent="text-emerald-600 bg-emerald-50"
        />
        <Metric
          icon={<Zap className="w-5 h-5" />}
          label="Slots tracked"
          value={totals.slots.toLocaleString()}
          // With more than one party size scraped, a bare total reads as one
          // number when it is really several searches added together.
          detail={
            totals.by_party.length > 1
              ? totals.by_party
                  .map((p) => `${p.slots.toLocaleString()} for ${p.party_size}`)
                  .join(' · ')
              : `${totals.bookable} bookable · ${totals.requestable} on request`
          }
        />
        <Metric
          icon={<AlertTriangle className="w-5 h-5" />}
          label="Needs attention"
          value={totals.needs_attention}
          detail="Failing, stale or never run"
          accent={
            totals.needs_attention > 0
              ? 'text-rose-600 bg-rose-50'
              : 'text-gray-400 bg-gray-50'
          }
        />
        <Metric
          icon={<Clock className="w-5 h-5" />}
          label="Freshest check"
          value={formatAge(totals.freshest_check_seconds)}
          detail={`Oldest ${formatAge(totals.oldest_check_seconds)}`}
          accent="text-amber-600 bg-amber-50"
        />
      </div>

      {/* ---------------- Status distribution ---------------- */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <MicroLabel>Fleet status</MicroLabel>
          <span className="text-[11px] text-gray-400 tabular-nums">
            {totals.sources} sources · {totals.venues} venues
          </span>
        </div>
        <div className="flex h-2.5 rounded-full overflow-hidden bg-gray-100 mb-4">
          {STATUS_ORDER.map((status) => {
            const count = statusCounts[status] || 0;
            if (!count) return null;
            return (
              <div
                key={status}
                className={`${STATUS_STYLES[status].bar} transition-all duration-500`}
                style={{ width: `${(count / totals.sources) * 100}%` }}
                title={`${STATUS_STYLES[status].label}: ${count}`}
              />
            );
          })}
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          {STATUS_ORDER.map((status) => {
            const count = statusCounts[status] || 0;
            if (!count) return null;
            return (
              <button
                key={status}
                onClick={() => setStatusFilter(statusFilter === status ? 'all' : status)}
                className={`flex items-center gap-2 text-xs transition-opacity ${
                  statusFilter === 'all' || statusFilter === status
                    ? 'opacity-100'
                    : 'opacity-40 hover:opacity-70'
                }`}
              >
                <LiveDot className={STATUS_STYLES[status].dot} />
                <span className="text-gray-600">{STATUS_STYLES[status].label}</span>
                <span className="text-gray-900 font-medium tabular-nums">{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ---------------- Providers + runs ---------------- */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 mb-6 items-start">
        <div className="xl:col-span-2 bg-white rounded-xl shadow-sm p-6">
          <MicroLabel>Providers</MicroLabel>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {data.providers.map((provider) => (
              <button
                key={provider.name}
                onClick={() =>
                  setProviderFilter(providerFilter === provider.name ? 'all' : provider.name)
                }
                className={`text-left rounded-lg border p-4 transition-all duration-200 ${
                  providerFilter === provider.name
                    ? 'border-blue-300 bg-blue-50/50 shadow-sm'
                    : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50/50'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">{provider.label}</span>
                    {provider.errors > 0 ? (
                      <AlertTriangle className="w-3.5 h-3.5 text-rose-500" />
                    ) : (
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                    )}
                  </div>
                  <span className="text-[10px] text-gray-400 tabular-nums">
                    {formatAge(provider.last_checked_age_seconds)}
                  </span>
                </div>

                <div className="flex items-baseline gap-4 mb-3">
                  <div>
                    <p className="text-xl font-semibold text-gray-900 tabular-nums leading-none">
                      {provider.slots}
                    </p>
                    <MicroLabel>slots</MicroLabel>
                  </div>
                  <div>
                    <p className="text-xl font-semibold text-gray-900 tabular-nums leading-none">
                      {provider.healthy}/{provider.sources}
                    </p>
                    <MicroLabel>healthy</MicroLabel>
                  </div>
                  <div>
                    <p className="text-xl font-semibold text-gray-900 tabular-nums leading-none">
                      {formatDuration(provider.avg_duration_ms)}
                    </p>
                    <MicroLabel>avg run</MicroLabel>
                  </div>
                </div>

                <div className="flex flex-wrap gap-1.5">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-gray-200 bg-gray-50 text-[10px] text-gray-500">
                    {provider.requires_browser ? (
                      <><MonitorSmartphone className="w-3 h-3" /> headless browser</>
                    ) : (
                      <><Globe className="w-3 h-3" /> direct API</>
                    )}
                  </span>
                  {provider.uses_proxy && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-gray-200 bg-gray-50 text-[10px] text-gray-500">
                      <Radio className="w-3 h-3" /> proxy rotation
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6">
          <MicroLabel>Recent runs</MicroLabel>
          <div className="mt-4 space-y-3">
            {data.recent_runs.length === 0 && (
              <p className="text-xs text-gray-400">No runs recorded yet.</p>
            )}
            {data.recent_runs.map((run) => (
              <div
                key={run.run_id}
                className="flex items-center justify-between pb-3 border-b border-gray-50 last:border-0 last:pb-0"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <LiveDot className={run.errors > 0 ? 'bg-rose-500' : 'bg-emerald-500'} />
                    <span className="text-xs font-mono text-gray-500">{run.run_id}</span>
                    {run.party_sizes.length > 0 && (
                      <span className="px-1.5 py-0.5 rounded bg-gray-100 text-[10px]
                                       font-semibold text-gray-600">
                        {run.party_sizes.join('/')}p
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1 ml-4">
                    {run.venues} venues · {run.slots} slots
                    {run.errors > 0 && ` · ${run.errors} errors`}
                  </p>
                </div>
                <div className="text-right flex-shrink-0 ml-3">
                  <p className="text-[11px] text-gray-600 tabular-nums">
                    {formatAge(run.age_seconds)}
                  </p>
                  <p className="text-[10px] text-gray-400 tabular-nums">
                    {run.wall_seconds !== null ? `${run.wall_seconds}s` : '—'}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-5 pt-4 border-t border-gray-50 flex items-center gap-2 text-[10px] text-gray-400">
            <Database className="w-3 h-3 flex-shrink-0" />
            <span className="truncate" title={database.path}>
              {database.exists ? formatBytes(database.size_bytes) : 'database missing'}
            </span>
          </div>
        </div>
      </div>

      {/* ---------------- Venue table ---------------- */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Sources</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              Showing {venues.length} of {data.venues.length}
              {(statusFilter !== 'all' || providerFilter !== 'all') && ' (filtered)'}
            </p>
          </div>

          <div className="flex items-center gap-2">
            {(statusFilter !== 'all' || providerFilter !== 'all' || query) && (
              <button
                onClick={() => { setStatusFilter('all'); setProviderFilter('all'); setQuery(''); }}
                className="text-xs text-gray-400 hover:text-gray-600 px-2 py-1"
              >
                Clear
              </button>
            )}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-300" />
              <input
                type="text"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search venue or slug…"
                className="pl-9 pr-3 py-2 w-full sm:w-64 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 transition"
              />
            </div>
          </div>
        </div>

        {/* Column headings — hidden on mobile where rows stack */}
        <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-2.5 bg-gray-50/60">
          <div className="col-span-4"><MicroLabel>Venue</MicroLabel></div>
          <div className="col-span-2"><MicroLabel>Status</MicroLabel></div>
          <div className="col-span-2"><MicroLabel>Slots</MicroLabel></div>
          <div className="col-span-2"><MicroLabel>Next days</MicroLabel></div>
          <div className="col-span-2 text-right"><MicroLabel>Last check</MicroLabel></div>
        </div>

        {venues.length === 0 ? (
          <div className="px-5 py-16 text-center">
            <Pause className="w-8 h-8 text-gray-200 mx-auto mb-3" />
            <p className="text-sm text-gray-400">No sources match these filters.</p>
          </div>
        ) : (
          venues.map((venue) => <VenueRow key={venue.id} venue={venue} />)
        )}
      </div>

      <p className="text-[11px] text-gray-400 text-center mt-6 tabular-nums">
        Snapshot taken {new Date(data.generated_at).toLocaleTimeString()} · auto-refreshing every {REFRESH_SECONDS}s
      </p>
    </div>
  );
};

export default ScraperDashboard;
